import { Component, ElementRef, forwardRef, Inject, Input, NgZone, OnDestroy, OnInit } from "@angular/core";
import * as _ from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject, Subscription } from "rxjs";
import { AlertService } from "../../dashboard-area/shared/alert.service";
import { Alert, AlertDefinitionRemedy, AlertRemedySelection } from "../../model";
import { AuthenticationService } from '../../service/authentication.service';
import { ContextService } from "../../service/context.service";
import { SocketService } from "../../service/socket.service";
import { AbstractThingContextService } from "../../shared/class/abstract-thing-context-service.class";
import { ActiveAlertListWidgetV2Service } from "../alert-list-v2/active-alert-list-v2/active-alert-list-widget-v2.service";
import { HistoricalAlertListWidgetV2Service } from "../alert-list-v2/historical-alert-list-v2/historical-alert-list-widget-v2.service";
import { WidgetPrintResizeCoordinatorService } from "../shared/widget-print-resize-coordinator.service";
import { ThingListWidgetV2Service } from "../thing-list/thing-list-widget-v2.service";
import { ThingTroubleshootingWidgetService } from "./thing-troubleshooting-widget.service";

@Component({
    selector: 'thing-troubleshooting-widget',
    template: require('./thing-troubleshooting-widget.component.html'),
    providers: [ThingTroubleshootingWidgetService, AlertService, ActiveAlertListWidgetV2Service, HistoricalAlertListWidgetV2Service, ThingListWidgetV2Service]
})
export class ThingTroubleshootingWidgetComponent implements OnInit, OnDestroy {

    @Input() title: string;

    @Input() loadPeriod: string;

    @Input() sendRemedyEnabled: boolean;

    @Input() description: string;

    constructor(
        @Inject(forwardRef(() => AlertService)) private alertService: AlertService,
        @Inject(forwardRef(() => ThingTroubleshootingWidgetService)) private thingTroubleshootingWidgetService: ThingTroubleshootingWidgetService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => ContextService)) private timezoneContextService: ContextService,
        @Inject(forwardRef(() => ElementRef)) private elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) private widgetPrintResizeCoordinator: WidgetPrintResizeCoordinatorService,
        @Inject(forwardRef(() => ActiveAlertListWidgetV2Service)) private activeAlertListWidgetV2Service: ActiveAlertListWidgetV2Service,
        @Inject(forwardRef(() => HistoricalAlertListWidgetV2Service)) private historicalAlertListWidgetV2Service: HistoricalAlertListWidgetV2Service,
        @Inject(forwardRef(() => NgZone)) private zone: NgZone,
        @Inject(forwardRef(() => SocketService)) private socketService: SocketService
    ) { }

    activeAndHistoricalList: Alert[] = [];
    solvedHistoricalList: Alert[] = [];
    remediesByAlertDef: { [alertDefinitionId: string]: AlertDefinitionRemedy[] } = {};
    showSolved: boolean;
    loading: boolean;
    userEmails: string[];
    activeAndHistoricalGroupByAlertName: { [alertName: string]: Alert[] };
    timezone: string;

    private _unregisterPrintResize: (() => void) | null = null;
    private alertDefIds = new Set<string>();
    private alertSubscription: Subscription;
    private alertsSubscriptionId: number;

    ngOnInit() {
        this._unregisterPrintResize = this.widgetPrintResizeCoordinator.subscribeElement(this.elementRef);
        this.timezone = this.timezoneContextService.getTimezone();
        this.loading = true;
        this.alertSubscription = this.subscribeToActiveAlerts().subscribe(alerts => {
            if (alerts) {
                this.loading = true;
                this.getRemedies(alerts).then(() => {
                    alerts.forEach(a => {
                        const remedies = this.remediesByAlertDef[a.alertDefinitionId];
                        if (remedies && remedies.length > 0) {
                            const alertDefinition = remedies[0].alertDefinition;
                            a.technicalDescription = alertDefinition.technicalDescription;
                        }
                    });
                    this.getHistoricalAlertsV2().then(historicalAlerts => {
                        this.getRemedies(historicalAlerts).then(() => {
                            let solved = [];
                            let unsolved = [];
                            this.filterSolved(historicalAlerts).forEach(h => {
                                if (this.isSolved(h.alertRemedySelections)) {
                                    solved.push(h);
                                } else {
                                    unsolved.push(h);
                                }
                            })
                            const activeAndHistoricalList = (this.filterSolved(alerts).concat(unsolved)).sort(this.compareAlerts);
                            this.activeAndHistoricalGroupByAlertName = _.groupBy(activeAndHistoricalList, "name");
                            this.activeAndHistoricalList = [];
                            Object.keys(this.activeAndHistoricalGroupByAlertName).forEach(key => {
                                if (this.activeAndHistoricalGroupByAlertName[key] && this.activeAndHistoricalGroupByAlertName[key].length) {
                                    this.activeAndHistoricalList.push(this.activeAndHistoricalGroupByAlertName[key][0]);
                                }
                            })
                            this.solvedHistoricalList = solved.sort(this.compareAlerts);
                            this.loading = false;
                        });
                    });
                });
            }
        });
        this.thingTroubleshootingWidgetService.getUsers().then(users => {
            this.userEmails = users.map(u => u.email);
        }).catch(() => { /* NO AUTH - DO NOTHING */ });
    }

    private subscribeToActiveAlerts(): BehaviorSubject<Alert[]> {
        let subject = new BehaviorSubject(null);
        this.getActiveAlertsV2()
            .then(alerts => {
                this.zone.run(() => subject.next(alerts));
                const topic = this.alertService.getAlertsTopic();
                this.alertsSubscriptionId = this.socketService.subscribe({
                    topic,
                    callback: () => {
                        this.getActiveAlertsV2().then(alerts => this.zone.run(() => subject.next(alerts)));
                    }
                });
            }).catch(err => console.error(err));
        return subject;
    }

    private getActiveAlertsV2(): Promise<Alert[]> {
        const contextAlert = this.thingContextService.getCurrentAlert();
        if (contextAlert) {
            if (contextAlert.endTimestamp) {
                return Promise.resolve([]);
            }
            return Promise.resolve([contextAlert]);
        }
        return this.activeAlertListWidgetV2Service.getPagedList(0, 100, null, null, null, null, null, false).then(pagedResult => {
            return pagedResult.content;
        });
    }

    private getHistoricalAlertsV2(): Promise<Alert[]> {
        const contextAlert = this.thingContextService.getCurrentAlert();
        if (contextAlert) {
            if (contextAlert.endTimestamp) {
                return Promise.resolve([contextAlert]);
            }
            return Promise.resolve([]);
        }
        if (this.loadPeriod) {
            const historicalStartDate = moment().subtract(moment.duration(this.loadPeriod)).valueOf();
            return this.historicalAlertListWidgetV2Service.getPagedList(0, 100, null, null, null, { startTimestamp: historicalStartDate }, null, false).then(pagedResult => {
                return pagedResult.content;
            });
        } else {
            return Promise.resolve([]);
        }
    }

    private compareAlerts(a1: Alert, a2: Alert) {
        return a2.startTimestamp - a1.startTimestamp;
    }

    private filterSolved(alerts: Alert[]): Alert[] {
        return alerts.filter(a => this.remediesByAlertDef[a.alertDefinitionId] && this.remediesByAlertDef[a.alertDefinitionId].length);
    }

    private isSolved(alertRemedySelections: AlertRemedySelection[]): boolean {
        if (alertRemedySelections && alertRemedySelections.length) {
            return alertRemedySelections[alertRemedySelections.length - 1] && alertRemedySelections[alertRemedySelections.length - 1].solved;
        } else {
            return false;
        }
    }

    updateSelection(alertIndex: number, alertRemedySelections: AlertRemedySelection[], fromSolved: boolean): void {
        let alert = fromSolved ? this.solvedHistoricalList[alertIndex] : this.activeAndHistoricalList[alertIndex];
        const timestamp = new Date().getTime();
        const userId = this.authenticationService.getUser().id;
        alertRemedySelections.forEach(s => { s.userId = userId; s.timestamp = timestamp; });
        if (fromSolved) {
            this.alertService.updateSelection(alert, alertRemedySelections).then(() => { // move solved/unsolved only historical
                if (alert.endTimestamp && !this.isSolved(alertRemedySelections)) {
                    this.solvedHistoricalList.splice(alertIndex, 1);
                    alert.alertRemedySelections = alertRemedySelections;
                    if (this.activeAndHistoricalGroupByAlertName[alert.name] && this.activeAndHistoricalGroupByAlertName[alert.name].length) {
                        this.activeAndHistoricalGroupByAlertName[alert.name].push(alert);
                    } else {
                        this.activeAndHistoricalGroupByAlertName[alert.name] = [alert];
                        this.activeAndHistoricalList.push(alert);
                    }
                }
            });
        } else {
            let promises = [];
            const alertsByName = _.cloneDeep(this.activeAndHistoricalGroupByAlertName[alert.name]);
            alertsByName.forEach(alert => {
                promises.push(this.alertService.updateSelection(alert, alertRemedySelections));
            })
            Promise.all(promises).then(() => { // move solved/unsolved only historical
                alertsByName.forEach((alert, index) => {
                    if (alert.endTimestamp && this.isSolved(alertRemedySelections)) {
                        if (index == 0) {
                            this.activeAndHistoricalList.splice(alertIndex, 1);
                        }
                        this.activeAndHistoricalGroupByAlertName[alert.name].splice(this.activeAndHistoricalGroupByAlertName[alert.name].indexOf(alert), 1);
                        alert.alertRemedySelections = _.cloneDeep(alertRemedySelections);
                        this.solvedHistoricalList.push(alert);
                    }
                });
            });
        }
    }

    private getRemedies(alerts: Alert[]): Promise<void> {
        alerts.forEach(alert => this.alertDefIds.add(alert.alertDefinitionId));
        let remedyRequests: Promise<AlertDefinitionRemedy[]>[] = [];
        let alertsIdsWithoutRemedies = [];
        this.alertDefIds.forEach(alertDefId => {
            if (!this.remediesByAlertDef[alertDefId]) {
                remedyRequests.push(this.thingTroubleshootingWidgetService.getRemediesByAlertDefinitionId(alertDefId));
                alertsIdsWithoutRemedies.push([alertDefId]);
            }
        });
        return Promise.all(remedyRequests).then(
            remediesResponse => {
                remediesResponse.forEach((r, i) => {
                    this.remediesByAlertDef[alertsIdsWithoutRemedies[i]] = r;
                });
            });
    }

    ngOnDestroy(): void {
        this._unregisterPrintResize?.();
        this._unregisterPrintResize = null;
        if (this.alertSubscription) {
            this.alertSubscription.unsubscribe();
            this.alertSubscription = null;
        }
        if (this.alertsSubscriptionId) {
            this.socketService.delete(this.alertsSubscriptionId);
            this.alertsSubscriptionId = null;
        }
    }
}